import EventEmitter from 'src/helpers/EventEmitter'
import AudioTrack from './tracks/AudioTrack'
import WebAudioTrack from './tracks/WebAudioTrack'
import VideoTrack from './tracks/VideoTrack'
import HlsVideoTrack from './tracks/HlsVideoTrack'
import BandwidthService from 'src/services/BandwidthService'

// HLS
import Hls from 'hls.js/dist/hls.light.min'
const hlsSupported = Hls.isSupported()

// CONSTANTS
const webAudioSupported =
  (window.AudioContext || window.webkitAudioContext) !== null
const isIOS = /(iOS|iPod|iPad|iPhone)/i.test(navigator.userAgent)

const Events = {
  BUFFERING: 'buffering',
  UNBUFFERING: 'unbuffering',
  ENDED: 'ended',
}

const stubVideoTrackConfig = {
  type: 'video',
  src: [
    {
      url: null,
      bitrate: 500,
    },
  ],
  canvasReady: false,
  preload: 'buffer',
}

function arraymove(arr, fromIndex, toIndex) {
  var element = arr[fromIndex]
  arr.splice(fromIndex, 1)
  arr.splice(toIndex, 0, element)
}

// MEDIA
class Media {
  constructor(config) {
    Object.assign(this, EventEmitter)

    // OBJECTS
    this.id = config.id
    this.tracks = []
    this.videoTrack = null
    this.audioTracks = []

    // STUB VIDEO TRACK
    let videoTrackConfig = config.tracks.filter(
      (track) => track.type === 'video'
    )[0]
    if (!videoTrackConfig) {
      config.tracks.push(stubVideoTrackConfig)
    }

    // CREATE TRACKS
    for (let trackConfig of config.tracks) {
      let track = ''
      switch (trackConfig.type) {
        case 'video': {
          // Hls
          let useHls = true
          if (!hlsSupported) useHls = false
          if (isIOS && trackConfig.canvasReady) useHls = false
          if (!trackConfig.src.filter((src) => src.hls)[0]) useHls = false

          if (useHls) {
            track = new HlsVideoTrack(trackConfig)
          } else {
            // Select track by bitrate
            this._orderTrackByBitrate(trackConfig, BandwidthService.kbps)

            // Init track
            track = new VideoTrack(trackConfig)
          }
          this.videoTrack = track
          break
        }

        case 'audio': {
          if (webAudioSupported) {
            track = new WebAudioTrack(trackConfig)
          } else {
            track = new AudioTrack(trackConfig)
          }
          this.audioTracks.push(track)
          break
        }
      }

      if (track) this.tracks.push(track)
    }

    // STATE
    this.isMuted = false
    this.shouldLoop = config.loop || false

    this.currAudioTrack = this.audioTracks[0]

    // EVENTS
    this.videoTrack.el.addEventListener('ended', this._onEnded)
  }

  _orderTrackByBitrate = (config, targetKbps) => {
    config.src.sort((a, b) => {
      return a.size - b.size
    })

    for (let idx in config.src) {
      let src = config.src[idx]
      if (src.hls) continue
      if (!src.size) continue
      let requiredKbps = (src.size * 8) / config.duration
      if (requiredKbps < targetKbps) {
        arraymove(config.src, idx, 0)
      }
    }
  }

  _onEnded = () => {
    this.emit(Events.ENDED)
    if (this.shouldLoop) {
      this.currentTime = 0
      this.play()
    }
  }

  _onBuffering = () => {
    this.videoTrack.el.addEventListener('playing', this._onUnbuffering)
    for (let track of this.audioTracks) {
      track.pause()
    }
    this.emit(Events.BUFFERING)
  }

  _onUnbuffering = () => {
    this.videoTrack.el.removeEventListener('playing', this._onUnbuffering)
    for (let track of this.audioTracks) {
      track.currentTime = this.videoTrack.currentTime
      track.play()
    }
    this.emit(Events.UNBUFFERING)
  }

  _onVisibilityChange = () => {
    if (document['hidden']) {
      for (let track of this.tracks) {
        track.pause()
      }
    } else {
      for (let track of this.tracks) {
        track.play()
      }
    }
  }

  // ************************************************************
  //
  // PUBLIC INTERFACE
  //
  // ************************************************************
  getDomElement = () => {
    return this.videoTrack.el
  }

  load = () => {
    let p = []
    for (let track of this.tracks) {
      p.push(track.load())
    }
    return Promise.all(p)
  }

  play = () => {
    if (this.ended) {
      this.currentTime = 0
    }

    for (let track of this.tracks) {
      // UNMUTE
      if (!this.isMuted) {
        switch (track.config.type) {
          case 'video':
            if (this.audioTracks.length === 0) {
              track.muted = false
            }
            break
          case 'audio':
            track.muted = false
            if (track === this.currAudioTrack) {
              track.volume = this.volume
            }
            break
        }
      }

      // ADD LISTENERS
      this.videoTrack.el.addEventListener('waiting', this._onBuffering)
      document.addEventListener('visibilitychange', this._onVisibilityChange)

      // PLAY
      track.play()
    }
  }

  pause = () => {
    if (this.paused) return

    this.videoTrack.el.removeEventListener('waiting', this._onBuffering)
    this.videoTrack.el.removeEventListener('playing', this._onUnbuffering)
    document.removeEventListener('visibilitychange', this._onVisibilityChange)

    for (let track of this.tracks) {
      track.pause()
    }
  }

  changeAudioTrackById = (id) => {
    let track = this.audioTracks.filter((track) => track.id === id)[0]
    if (!track) return
    if (this.currAudioTrack === track) return

    this.currAudioTrack.volume = 0
    this.currAudioTrack = track
    this.currAudioTrack.volume = this.volume
  }

  changeAudioTrackByIdx = (idx) => {
    let track = this.audioTracks[idx]
    if (!track) return
    if (this.currAudioTrack === track) return

    this.currAudioTrack.volume = 0
    this.currAudioTrack = track
    this.currAudioTrack.volume = this.volume
  }

  get volume() {
    return this.videoTrack.volume
  }

  set volume(val) {
    let vol = Math.max(Math.min(val, 1), 0)
    this.videoTrack.volume = vol
    this.currAudioTrack.volume = vol
  }

  get muted() {
    return this.isMuted
  }

  set muted(val) {
    this.isMuted = val

    if (this.audioTracks.length > 0) {
      for (let track of this.audioTracks) {
        track.muted = val
      }
    } else {
      this.videoTrack.muted = val
    }
  }

  get currentTime() {
    return this.videoTrack.currentTime
  }

  set currentTime(time) {
    for (let track of this.tracks) {
      track.currentTime = time
    }
  }

  get loop() {
    return this.shouldLoop
  }

  set loop(val) {
    this.shouldLoop = val
  }

  get duration() {
    return this.videoTrack.duration
  }

  get paused() {
    return this.videoTrack.paused
  }

  get ended() {
    return this.videoTrack.ended
  }
}

Media.Events = Events

export default Media
